import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CreditCard, Lock, ArrowLeft, Check, Loader2 } from 'lucide-react';
import { useCart } from '../context/CartContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { id: 1, label: 'Information' },
  { id: 2, label: 'Shipping' },
  { id: 3, label: 'Payment' },
];

const SHIPPING_OPTIONS = [
  { id: 'standard', label: 'Standard Shipping', detail: '5-7 business days', price: 0, complimentary: true },
  { id: 'express', label: 'Express Shipping', detail: '2-3 business days', price: 25 },
  { id: 'next_day', label: 'Next Day Delivery', detail: 'Order before 2pm', price: 45 },
];

const Checkout = () => {
  const navigate = useNavigate();
  const { cart, cartTotal } = useCart();
  const [step, setStep] = useState(1);
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState('');

  const [info, setInfo] = useState({
    email: '',
    firstName: '',
    lastName: '',
    address: '',
    apt: '',
    city: '',
    postal: '',
    country: 'France',
    phone: '',
  });

  const [shippingId, setShippingId] = useState('standard');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  const shipping = SHIPPING_OPTIONS.find((s) => s.id === shippingId);
  const shippingCost = shipping?.complimentary ? 0 : shipping?.price || 0;
  const total = cartTotal + shippingCost;

  const handleInfoSubmit = (e) => {
    e.preventDefault();
    setStep(2);
  };

  const handleStripeCheckout = async () => {
    setError('');
    setRedirecting(true);
    try {
      const payload = {
        items: cart.map((c) => ({
          id: c.id,
          quantity: c.quantity,
          size: c.size,
          color: c.color,
        })),
        shipping_method: shippingId,
        contact: info,
        origin_url: window.location.origin,
      };
      const { data } = await axios.post(`${API}/checkout/session`, payload);
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received from server.');
      }
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        'Unable to start payment. Please try again.';
      setError(typeof msg === 'string' ? msg : 'Unable to start payment.');
      setRedirecting(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <p className="font-display text-3xl mb-4">Your bag is empty</p>
        <p className="text-sm text-neutral-500 mb-8">
          Add a piece to your bag to begin checkout.
        </p>
        <Link
          to="/shop"
          className="inline-block bg-neutral-900 text-white px-10 py-4 text-[11px] tracking-[0.22em] uppercase hover:bg-neutral-700 transition-colors"
        >
          Shop Now
        </Link>
      </div>
    );
  }

  return (
    <div className="pt-28 pb-32 bg-white min-h-screen">
      <div className="max-w-[1300px] mx-auto px-6 lg:px-12">
        {/* Stepper */}
        <div className="flex items-center justify-center gap-3 sm:gap-6 pt-8 pb-12">
          {STEPS.map((s, i) => {
            const active = step === s.id;
            const completed = step > s.id;
            return (
              <React.Fragment key={s.id}>
                <button
                  onClick={() => completed && setStep(s.id)}
                  className="flex items-center gap-3 group"
                  disabled={!completed}
                >
                  <span
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] border transition-colors ${
                      active
                        ? 'border-neutral-900 text-neutral-900'
                        : completed
                        ? 'bg-neutral-900 border-neutral-900 text-white'
                        : 'border-neutral-300 text-neutral-400'
                    }`}
                  >
                    {completed ? <Check className="w-3.5 h-3.5" strokeWidth={2} /> : s.id}
                  </span>
                  <span
                    className={`text-[11px] tracking-[0.22em] uppercase hidden sm:inline ${
                      active ? 'text-neutral-900' : completed ? 'text-neutral-900' : 'text-neutral-400'
                    }`}
                  >
                    {s.label}
                  </span>
                </button>
                {i < STEPS.length - 1 && (
                  <span className={`w-8 sm:w-16 h-px ${step > s.id ? 'bg-neutral-900' : 'bg-neutral-200'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-12 lg:gap-16 mt-6">
          {/* Left: Form */}
          <div>
            {step === 1 && (
              <form onSubmit={handleInfoSubmit}>
                <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mb-10">
                  Contact Information
                </h2>

                <Field label="Email" required>
                  <input
                    type="email"
                    required
                    value={info.email}
                    onChange={(e) => setInfo({ ...info, email: e.target.value })}
                    placeholder="your@email.com"
                    className={inputClass}
                  />
                </Field>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field label="First Name" required>
                    <input
                      type="text"
                      required
                      value={info.firstName}
                      onChange={(e) => setInfo({ ...info, firstName: e.target.value })}
                      placeholder="Jane"
                      className={inputClass}
                    />
                  </Field>
                  <Field label="Last Name" required>
                    <input
                      type="text"
                      required
                      value={info.lastName}
                      onChange={(e) => setInfo({ ...info, lastName: e.target.value })}
                      placeholder="Doe"
                      className={inputClass}
                    />
                  </Field>
                </div>

                <Field label="Address" required>
                  <input
                    type="text"
                    required
                    value={info.address}
                    onChange={(e) => setInfo({ ...info, address: e.target.value })}
                    placeholder="123 Rue de Rivoli"
                    className={inputClass}
                  />
                </Field>

                <Field label="Apartment, suite, etc.">
                  <input
                    type="text"
                    value={info.apt}
                    onChange={(e) => setInfo({ ...info, apt: e.target.value })}
                    placeholder="Apt 4B (optional)"
                    className={inputClass}
                  />
                </Field>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field label="City" required>
                    <input
                      type="text"
                      required
                      value={info.city}
                      onChange={(e) => setInfo({ ...info, city: e.target.value })}
                      placeholder="Paris"
                      className={inputClass}
                    />
                  </Field>
                  <Field label="Postal Code" required>
                    <input
                      type="text"
                      required
                      value={info.postal}
                      onChange={(e) => setInfo({ ...info, postal: e.target.value })}
                      placeholder="75001"
                      className={inputClass}
                    />
                  </Field>
                </div>

                <Field label="Phone">
                  <input
                    type="tel"
                    value={info.phone}
                    onChange={(e) => setInfo({ ...info, phone: e.target.value })}
                    placeholder="+36 30 746 5419"
                    className={inputClass}
                  />
                </Field>

                <button
                  type="submit"
                  className="w-full bg-neutral-900 text-white py-4 text-[11px] tracking-[0.22em] uppercase hover:bg-neutral-700 transition-colors mt-6"
                >
                  Continue to Shipping
                </button>
              </form>
            )}

            {step === 2 && (
              <div>
                <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mb-10">
                  Shipping Method
                </h2>

                <div className="border border-neutral-200 px-6 py-5 mb-8 flex items-start justify-between">
                  <div>
                    <p className="text-[11px] tracking-[0.22em] uppercase text-neutral-500 mb-2">Ship To</p>
                    <p className="text-sm">
                      {info.firstName} {info.lastName}
                    </p>
                    <p className="text-sm text-neutral-600">
                      {info.address}{info.apt ? `, ${info.apt}` : ''}
                    </p>
                    <p className="text-sm text-neutral-600">
                      {info.city}, {info.postal} · {info.country}
                    </p>
                  </div>
                  <button
                    onClick={() => setStep(1)}
                    className="text-[11px] tracking-[0.22em] uppercase border-b border-neutral-900 pb-0.5 hover:opacity-60 transition-opacity"
                  >
                    Edit
                  </button>
                </div>

                <div className="space-y-3 mb-10">
                  {SHIPPING_OPTIONS.map((opt) => (
                    <label
                      key={opt.id}
                      className={`flex items-center justify-between p-5 border cursor-pointer transition-colors ${
                        shippingId === opt.id
                          ? 'border-neutral-900 bg-neutral-50'
                          : 'border-neutral-200 hover:border-neutral-400'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <span
                          className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                            shippingId === opt.id ? 'border-neutral-900' : 'border-neutral-300'
                          }`}
                        >
                          {shippingId === opt.id && (
                            <span className="w-2 h-2 rounded-full bg-neutral-900" />
                          )}
                        </span>
                        <div>
                          <p className="text-sm font-medium">{opt.label}</p>
                          <p className="text-xs text-neutral-500 mt-0.5">{opt.detail}</p>
                        </div>
                      </div>
                      <span className="text-sm">
                        {opt.complimentary ? (
                          <span className="text-neutral-500">Free</span>
                        ) : (
                          `$${opt.price}`
                        )}
                      </span>
                      <input
                        type="radio"
                        name="shipping"
                        value={opt.id}
                        checked={shippingId === opt.id}
                        onChange={() => setShippingId(opt.id)}
                        className="sr-only"
                      />
                    </label>
                  ))}
                </div>

                <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4">
                  <button
                    onClick={() => setStep(1)}
                    className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-neutral-700 hover:text-neutral-900"
                  >
                    <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to Information
                  </button>
                  <button
                    onClick={() => setStep(3)}
                    className="bg-neutral-900 text-white py-4 px-12 text-[11px] tracking-[0.22em] uppercase hover:bg-neutral-700 transition-colors"
                  >
                    Continue to Payment
                  </button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div>
                <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mb-10">
                  Review &amp; Pay
                </h2>

                <div className="border border-neutral-200 mb-8">
                  <SummaryRow
                    label="Contact"
                    value={info.email}
                    onEdit={() => setStep(1)}
                  />
                  <SummaryRow
                    label="Ship To"
                    value={`${info.address}${info.apt ? `, ${info.apt}` : ''}, ${info.city}, ${info.postal}`}
                    onEdit={() => setStep(1)}
                    border
                  />
                  <SummaryRow
                    label="Method"
                    value={
                      <>
                        {shipping?.label} ·{' '}
                        <span className="text-neutral-500">
                          {shipping?.complimentary ? 'Free' : `$${shipping?.price}`}
                        </span>
                      </>
                    }
                    onEdit={() => setStep(2)}
                    border
                  />
                </div>

                <div className="bg-[#fafaf8] border border-neutral-200 px-6 py-5 mb-8 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-neutral-900 text-white flex items-center justify-center flex-shrink-0">
                    <CreditCard className="w-4 h-4" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-sm font-medium mb-1">Pay securely with Stripe</p>
                    <p className="text-xs text-neutral-600 leading-relaxed">
                      You will be redirected to Stripe&rsquo;s hosted page to complete your payment.
                      Cards, Apple Pay and Google Pay are accepted.
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-neutral-500 mb-6">
                  <Lock className="w-3.5 h-3.5" strokeWidth={1.5} /> Secure Payment via Stripe
                </div>

                {error && (
                  <p className="mb-5 text-sm text-red-600 border border-red-200 bg-red-50 px-4 py-3">
                    {error}
                  </p>
                )}

                <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4 mt-6">
                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    disabled={redirecting}
                    className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-neutral-700 hover:text-neutral-900 disabled:opacity-40"
                  >
                    <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to Shipping
                  </button>
                  <button
                    type="button"
                    onClick={handleStripeCheckout}
                    disabled={redirecting}
                    className="bg-neutral-900 text-white py-4 px-12 text-[11px] tracking-[0.22em] uppercase hover:bg-neutral-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {redirecting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> Redirecting…
                      </>
                    ) : (
                      <>Place Order · ${total}</>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Right: Order summary */}
          <aside className="lg:sticky lg:top-28 self-start">
            <div className="bg-neutral-50 p-7">
              <h3 className="font-display text-2xl font-medium mb-6">Order Summary</h3>

              <ul className="space-y-5 max-h-[420px] overflow-y-auto pr-1">
                {cart.map((item) => {
                  const key = `${item.id}-${item.size}-${item.color}`;
                  return (
                    <li key={key} className="flex gap-4">
                      <div className="w-16 h-20 bg-white flex-shrink-0 overflow-hidden">
                        <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium leading-tight truncate">{item.name}</p>
                        <p className="text-xs text-neutral-500 mt-1">
                          {item.color} · {item.size}
                        </p>
                        <p className="text-xs text-neutral-500 mt-0.5">
                          Quantity {item.quantity}
                        </p>
                      </div>
                      <p className="text-sm whitespace-nowrap">${(item.price * item.quantity).toFixed(2)}</p>
                    </li>
                  );
                })}
              </ul>

              <div className="mt-7 pt-6 border-t border-neutral-200 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-500">Subtotal</span>
                  <span>${cartTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-500">Shipping</span>
                  <span className={shipping?.complimentary ? 'text-neutral-500' : ''}>
                    {shipping?.complimentary ? 'Free' : `$${shippingCost.toFixed(2)}`}
                  </span>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-neutral-200 flex items-end justify-between">
                <span className="text-base">Total</span>
                <span className="text-2xl font-display">${total}</span>
              </div>
            </div>
            <p className="text-[11px] text-neutral-500 mt-4 text-center">
              All transactions are secure and encrypted.
            </p>
          </aside>
        </div>
      </div>
    </div>
  );
};

const inputClass =
  'w-full border border-neutral-300 px-4 py-3.5 text-sm focus:outline-none focus:border-neutral-900 transition-colors bg-white placeholder:text-neutral-400';

const Field = ({ label, required, children }) => (
  <div className="mb-5">
    <label className="block text-[11px] tracking-[0.22em] uppercase text-neutral-500 mb-2">
      {label} {required && <span className="text-neutral-700">*</span>}
    </label>
    {children}
  </div>
);

const SummaryRow = ({ label, value, onEdit, border }) => (
  <div className={`flex items-start justify-between px-6 py-4 ${border ? 'border-t border-neutral-200' : ''}`}>
    <div className="flex items-start gap-6 flex-1">
      <p className="text-[11px] tracking-[0.22em] uppercase text-neutral-500 w-20 mt-0.5">{label}</p>
      <p className="text-sm text-neutral-700">{value}</p>
    </div>
    <button
      type="button"
      onClick={onEdit}
      className="text-[11px] tracking-[0.22em] uppercase border-b border-neutral-900 pb-0.5 ml-4 flex-shrink-0"
    >
      Edit
    </button>
  </div>
);

export default Checkout;
